// src/pages/ScoringResultsPage.js
// Updated: 11/09/2025 10:10 PM EST
// --- THIS IS THE FULL, UPDATED FILE (WITH DEBUG LOGS + SAFETY GUARDS) ---

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc as fsDoc, getDoc, collection, query, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Award, BarChart3, X } from 'lucide-react';
import { calculateResults } from '../utils/scoringCalculator';
import { PackageBarChart, PackageBoxPlot } from '../components/ScoringAnalyticsCharts';

const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

const LoadingSpinner = () => (
  <div className="flex h-64 items-center justify-center">
    <div className="h-12 w-12 animate-spin rounded-full border-4 border-dashed border-blue-500"></div>
  </div>
);

const AnalyticsModal = ({ isOpen, onClose, results }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl mx-4">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close analytics"
        >
          <X className="h-6 w-6" />
        </button>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Scoring Analytics</h2>

        <div className="max-h-[70vh] overflow-y-auto space-y-8">
          <div>
            <PackageBarChart data={results} />
          </div>
          <div className="border-t pt-8">
            <PackageBoxPlot data={results} />
          </div>
        </div>

        <div className="mt-6 flex justify-end border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Safe number formatter for UI (prevents toFixed on undefined/NaN)
const fmtFixed = (value, digits = 2) =>
  Number.isFinite(value) ? value.toFixed(digits) : '—';

export default function ScoringResultsPage() {
  const { sessionId } = useParams();
  const { userData } = useAuth();

  const [session, setSession] = useState(null);
  const [calculatedResults, setCalculatedResults] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    let isActive = true; // guard against state updates after unmount

    const fetchData = async () => {
      if (!sessionId || !userData?.orgId) {
        if (isActive) {
          setError('Missing Session ID or User Organization information.');
          setIsLoading(false);
        }
        return;
      }

      if (isActive) {
        setIsLoading(true);
        setError('');
      }

      try {
        const sessionRef = fsDoc(db, 'scoringSessions', sessionId);
        const sessionSnap = await getDoc(sessionRef);

        if (!sessionSnap.exists()) {
          throw new Error('Scoring session not found.');
        }

        const sessionData = { id: sessionSnap.id, ...sessionSnap.data() };

        // Basic access check
        if (sessionData.orgId !== userData.orgId) {
          throw new Error('Access denied for this scoring session.');
        }

        const packagesQuery = query(collection(db, 'scoringSessions', sessionId, 'packages'));
        const packagesSnap = await getDocs(packagesQuery);
        const packagesData = packagesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        const scoresQuery = query(collection(db, 'scoringSessions', sessionId, 'scores'));
        const scoresSnap = await getDocs(scoresQuery);
        const scoresData = scoresSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // --- DEBUG LOGS ---
        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('--- DEBUG: ScoringResultsPage Data Fetch ---');
          // eslint-disable-next-line no-console
          console.log('Session (must have .categories):', sessionData);
          // eslint-disable-next-line no-console
          console.log('Packages (must not be empty):', packagesData);
          // eslint-disable-next-line no-console
          console.log('Scores (must not be empty):', scoresData);
        }

        const results = calculateResults(sessionData, packagesData, scoresData) || [];

        if (isDev) {
          // eslint-disable-next-line no-console
          console.log('Calculated Results (must not be empty for button to show):', results);
        }

        if (!isActive) return;
        setSession(sessionData);
        setCalculatedResults(results);
      } catch (err) {
        if (isDev) {
          // eslint-disable-next-line no-console
          console.error('Error fetching results:', err);
        }
        if (!isActive) return;
        setError(err?.message || 'Failed to load results.');
      } finally {
        if (isActive) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [sessionId, userData?.orgId]);

  // Derive max possible values from first result (as your calculator seems to provide),
  // with safe guards so header formatting never throws.
  const { maxPossibleRaw, maxPossibleWeighted } = useMemo(() => {
    const r0 = calculatedResults?.[0] || {};
    const raw = Number.isFinite(r0.totalPossibleRaw) ? r0.totalPossibleRaw : 0;
    const weighted = Number.isFinite(r0.totalPossibleWeighted) ? r0.totalPossibleWeighted : 0;
    return { maxPossibleRaw: raw, maxPossibleWeighted: weighted };
  }, [calculatedResults]);

  if (isLoading) return <LoadingSpinner />;
  if (error) {
    return (
      <p className="text-red-600 p-4 bg-red-50 border border-red-200 rounded">
        {error}
      </p>
    );
  }
  if (!session) {
    return <p className="text-gray-500 p-4">Session data not available.</p>;
  }

  const hasResults = Array.isArray(calculatedResults) && calculatedResults.length > 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div>
          <Link
            to="/scoring"
            className="mb-2 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Scoring Sessions
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">Scoring Results</h1>
        </div>

        {hasResults && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
          >
            <BarChart3 className="mr-2 h-5 w-5" />
            View Analytics
          </button>
        )}
      </div>

      <h2 className="mb-6 text-xl font-semibold text-blue-700">{session.name}</h2>

      {!hasResults ? (
        <p className="text-gray-500 p-4 bg-gray-50 border rounded">
          No scores submitted yet or no packages found for this session.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Rank
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Package
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Reviewers
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Avg. Raw Score (/ {fmtFixed(maxPossibleRaw, 0)})
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500"
                >
                  Avg. Weighted Score (/ {fmtFixed(maxPossibleWeighted, 1)})
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200 bg-white">
              {calculatedResults.map((result, index) => (
                <tr
                  key={result.id}
                  className={
                    result.rank === 1
                      ? 'bg-yellow-50'
                      : index % 2 === 0
                      ? 'bg-white'
                      : 'bg-gray-50'
                  }
                >
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-semibold text-gray-900">
                    <div className="flex items-center">
                      {result.rank === 1 && (
                        <Award className="mr-2 h-5 w-5 text-yellow-500" />
                      )}
                      #{result.rank}
                    </div>
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-800">
                    {result.name}
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm text-center text-gray-500">
                    {Number.isFinite(result.numReviewers) ? result.numReviewers : '—'}
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm text-right text-gray-500">
                    {fmtFixed(result.avgRawScore, 2)}
                  </td>

                  <td className="whitespace-nowrap px-6 py-4 text-sm text-right font-bold text-gray-700">
                    {fmtFixed(result.avgWeightedScore, 2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AnalyticsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        results={calculatedResults}
      />
    </div>
  );
}
